import dayjs from "dayjs";
import { shiftIdentityColor } from "./shiftRoute";

/** A robot or rig on none of the board's shifts — idle equipment reads as gray. */
export const IDLE_VEHICLE_COLOR = "#9e9e9e";

/** The slice of a shift that places vehicles on it. */
export interface VehicleShift {
	id?: string;
	robotId?: string;
	robotName?: string;
	rigName?: string;
	scheduledDate?: Date;
}

export interface VehicleShiftColors {
	colorByRobotId: ReadonlyMap<string, string>;
	/** Keyed by NAME, one entry per rig: the trucks that draw a rig come from another Airtable base
	 *  (Equipment), whose records share only the rig's name with the Rigs table a shift links to. */
	colorByRigName: ReadonlyMap<string, string>;
}

/**
 * Each robot and rig on the given shifts wears its shift's identity color, so the robot, its rig,
 * its jobs and its card read as one crew. A vehicle on several shifts takes the soonest one from
 * today: today first, then later days, then — only when nothing is upcoming — the most recent past
 * day. Vehicles on none of the shifts are absent; the map draws them in {@link IDLE_VEHICLE_COLOR}.
 *
 * `colorByShiftId` replaces the robot-derived identity color with an explicit one per shift (the
 * spaced scheme — see spacedShiftColors); a shift it doesn't list keeps its identity color.
 */
export function vehicleShiftColors(
	shifts: readonly VehicleShift[],
	now: Date,
	colorByShiftId?: ReadonlyMap<string, string>,
): VehicleShiftColors {
	const colorByRobotId = new Map<string, string>();
	const colorByRigName = new Map<string, string>();
	for (const shift of soonestFromToday(shifts, now)) {
		const color = (shift.id && colorByShiftId?.get(shift.id)) || shiftIdentityColor(shift.robotName);
		if (shift.robotId && !colorByRobotId.has(shift.robotId)) colorByRobotId.set(shift.robotId, color);
		for (const rigName of rigNames(shift.rigName)) {
			if (!colorByRigName.has(rigName)) colorByRigName.set(rigName, color);
		}
	}
	return { colorByRobotId, colorByRigName };
}

/** A shift's rigs. `Shifts."Rig #"` is a formula over a multipleRecordLinks lookup
 *  (`{Name (from Schedule Rig)}&""`), so two linked rigs arrive comma-joined in one string. */
function rigNames(rigName: string | undefined): string[] {
	if (!rigName) return [];
	return rigName
		.split(",")
		.map((name) => name.trim())
		.filter(Boolean);
}

/** Upcoming days soonest-first, then past days most-recent-first, then shifts without a Sched Date. */
function soonestFromToday(shifts: readonly VehicleShift[], now: Date): VehicleShift[] {
	const today = dayjs(now).startOf("day").valueOf();
	const rank = (shift: VehicleShift): [number, number] => {
		if (!shift.scheduledDate) return [2, 0];
		const day = dayjs(shift.scheduledDate).startOf("day").valueOf();
		return day >= today ? [0, day - today] : [1, today - day];
	};
	return shifts
		.map((shift) => ({ shift, rank: rank(shift) }))
		.sort((a, b) => a.rank[0] - b.rank[0] || a.rank[1] - b.rank[1])
		.map(({ shift }) => shift);
}
